import { Route } from '../types/route.types';
import { SkiTouringFilters } from '../types/filter.types';
import { FILTER_CONSTANTS } from '../constants/filterConstants';
import {
  SKI_RATING_SYSTEM,
  SKI_EXPOSITION_SYSTEM,
  SKI_GLOBAL_RATING_SYSTEM,
  SKI_PUNCTUAL_RATING_SYSTEM,
} from '../constants/skiRatingSystems';

/**
 * Utilitaires pour le filtrage
 */

/**
 * Vérifie si un filtre de cotation est actif
 */
export const isRatingFilterActive = (
  ratingFilter: { min: number | null; max: number | null } | undefined,
  maxIndex: number
): boolean => {
  if (!ratingFilter) return false;
  return (
    (ratingFilter.min !== null && ratingFilter.min !== undefined && ratingFilter.min > 0) ||
    (ratingFilter.max !== null && ratingFilter.max !== undefined && ratingFilter.max < maxIndex)
  );
};

/**
 * Vérifie si une valeur de filtre est active
 */
export const isFilterActive = (value: number | null | undefined, defaultValue: number): boolean => {
  return value !== null && value !== undefined && value !== defaultValue;
};

/**
 * Vérifie si une plage de filtre est active
 */
export const isRangeFilterActive = (
  filterRange: { min: number | null; max: number | null } | undefined,
  defaultValueMin: number,
  defaultValueMax: number
): boolean => {
  if (!filterRange) return false;
  return (
    isFilterActive(filterRange.min, defaultValueMin) ||
    isFilterActive(filterRange.max, defaultValueMax)
  );
};

/**
 * Vérifie si une valeur numérique est dans une plage
 */
export const matchesRangeFilter = (
  routeValue: number | null | undefined,
  filterRange: { min: number | null; max: number | null } | undefined,
  defaultValueMin: number,
  defaultValueMax: number
): boolean => {
  if (!filterRange) return true;

  const minActive = isFilterActive(filterRange.min, defaultValueMin);
  const maxActive = isFilterActive(filterRange.max, defaultValueMax);

  if (!minActive && !maxActive) return true;
  if (routeValue === null || routeValue === undefined) return false;

  const min = filterRange.min ?? defaultValueMin;
  const max = filterRange.max ?? defaultValueMax;

  return routeValue >= min && routeValue <= max;
};

/**
 * Vérifie si une cotation (string) est dans une plage d'indices
 */
export const matchesRatingFilter = (
  routeValue: string | null | undefined,
  filterRange: { min: number | null; max: number | null } | undefined,
  ratingSystem: readonly string[]
): boolean => {
  if (!filterRange) return true;

  const maxIndex = ratingSystem.length - 1;
  const minActive = isRatingFilterActive(filterRange, maxIndex);

  if (!minActive) return true;
  if (!routeValue) return false;

  const routeIndex = ratingSystem.indexOf(routeValue);
  if (routeIndex === -1) return false;

  const min = filterRange.min ?? 0;
  const max = filterRange.max ?? maxIndex;

  return routeIndex >= min && routeIndex <= max;
};

/**
 * Vérifie si au moins un filtre de ski est actif
 */
export const hasAnyActiveSkiFilter = (skiFilters: SkiTouringFilters): boolean => {
  return !!(
    isRatingFilterActive(skiFilters.skiRating, SKI_RATING_SYSTEM.length - 1) ||
    isRatingFilterActive(skiFilters.skiExposition, SKI_EXPOSITION_SYSTEM.length - 1) ||
    isRatingFilterActive(skiFilters.skiGlobalRating, SKI_GLOBAL_RATING_SYSTEM.length - 1) ||
    isRatingFilterActive(skiFilters.skiPunctualRating, SKI_PUNCTUAL_RATING_SYSTEM.length - 1) ||
    (skiFilters.configuration !== null && skiFilters.configuration !== undefined) ||
    isRangeFilterActive(skiFilters.elevationMax, 0, FILTER_CONSTANTS.MAX_ELEVATION) ||
    isRangeFilterActive(skiFilters.elevationMin, 0, FILTER_CONSTANTS.MAX_ELEVATION) ||
    isRangeFilterActive(skiFilters.heightDiffUp, 0, FILTER_CONSTANTS.MAX_HEIGHT_DIFF) ||
    isRangeFilterActive(skiFilters.heightDiffDown, 0, FILTER_CONSTANTS.MAX_HEIGHT_DIFF) ||
    isRangeFilterActive(skiFilters.heightDiffDifficulties, 0, FILTER_CONSTANTS.MAX_HEIGHT_DIFF_DIFFICULTIES) ||
    isRangeFilterActive(skiFilters.duration, FILTER_CONSTANTS.MIN_DURATION, FILTER_CONSTANTS.MAX_DURATION) ||
    (skiFilters.orientations !== undefined && skiFilters.orientations !== null && skiFilters.orientations.length > 0)
  );
};

/**
 * Vérifie si une route correspond aux filtres de ski de randonnée
 */
export const matchesSkiFilters = (route: Route, skiFilters: SkiTouringFilters): boolean => {
  if (!hasAnyActiveSkiFilter(skiFilters)) {
    return true;
  }

  // Vérifier chaque filtre individuellement
  if (!matchesSkiRatingsFilter(route, skiFilters)) return false;
  if (!matchesElevationFilter(route, skiFilters)) return false;
  if (!matchesHeightDiffFilter(route, skiFilters)) return false;
  if (!matchesDurationFilter(route, skiFilters)) return false;
  if (!matchesOrientationsFilter(route, skiFilters)) return false;

  return true;
};

/**
 * Vérifie si une route correspond aux filtres d'altitude
 */
const matchesElevationFilter = (route: Route, skiFilters: SkiTouringFilters): boolean => {
  return (
    matchesRangeFilter(route.elevationMax, skiFilters.elevationMax, 0, FILTER_CONSTANTS.MAX_ELEVATION) &&
    matchesRangeFilter(route.elevationMin, skiFilters.elevationMin, 0, FILTER_CONSTANTS.MAX_ELEVATION)
  );
};

/**
 * Vérifie si une route correspond aux filtres de dénivelé
 */
const matchesHeightDiffFilter = (route: Route, skiFilters: SkiTouringFilters): boolean => {
  return (
    matchesRangeFilter(route.heightDiffUp, skiFilters.heightDiffUp, 0, FILTER_CONSTANTS.MAX_HEIGHT_DIFF) &&
    matchesRangeFilter(route.heightDiffDown, skiFilters.heightDiffDown, 0, FILTER_CONSTANTS.MAX_HEIGHT_DIFF) &&
    matchesRangeFilter(route.heightDiffDifficulties, skiFilters.heightDiffDifficulties, 0, FILTER_CONSTANTS.MAX_HEIGHT_DIFF_DIFFICULTIES)
  );
};

/**
 * Vérifie si une route correspond aux filtres de durée
 */
const matchesDurationFilter = (route: Route, skiFilters: SkiTouringFilters): boolean => {
  return matchesRangeFilter(
    route.calculatedDuration,
    skiFilters.duration,
    FILTER_CONSTANTS.MIN_DURATION,
    FILTER_CONSTANTS.MAX_DURATION
  );
};

/**
 * Vérifie si une route correspond aux filtres de cotations ski
 */
const matchesSkiRatingsFilter = (route: Route, skiFilters: SkiTouringFilters): boolean => {
  return (
    matchesRatingFilter(route.skiRating, skiFilters.skiRating, SKI_RATING_SYSTEM) &&
    matchesRatingFilter(route.skiExposition, skiFilters.skiExposition, SKI_EXPOSITION_SYSTEM) &&
    matchesRatingFilter(route.labandeGlobalRating, skiFilters.skiGlobalRating, SKI_GLOBAL_RATING_SYSTEM) &&
    matchesRatingFilter(route.labandeSkiRating, skiFilters.skiPunctualRating, SKI_PUNCTUAL_RATING_SYSTEM)
  );
};

/**
 * Vérifie si une route correspond aux filtres d'orientation
 */
const matchesOrientationsFilter = (route: Route, skiFilters: SkiTouringFilters): boolean => {
  if (!skiFilters.orientations || skiFilters.orientations.length === 0) {
    return true;
  }

  if (!route.orientations || route.orientations.length === 0) {
    return false;
  }

  return route.orientations.some(orientation =>
    skiFilters.orientations!.includes(orientation)
  );
};
